import mixpanel from 'mixpanel-browser';
import { getMixpanelPlatformProperties } from './mixpanelPlatform';

const DEFAULT_MIXPANEL_API_HOST = 'https://api-eu.mixpanel.com';
const MIXPANEL_TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN || 'd91ef8dd1868f640848a61ac37193ebd';
const MIXPANEL_API_HOST = (
  (process.env.REACT_APP_MIXPANEL_API_HOST || '').trim() || DEFAULT_MIXPANEL_API_HOST
).replace(/\/+$/, '');
const MIXPANEL_ALLOWED_HOSTNAMES = new Set([
  'vivianlab.com',
  'www.vivianlab.com',
  'vivianlab.gr',
  'www.vivianlab.gr',
]);

let isInitialized = false;
let identifiedSharetribeUserId = null;
let lastRegisteredUserProps = null;
let hasLoggedMixpanelEndpoint = false;

const asString = value => (typeof value === 'string' ? value.trim() : '');
const getCurrentHostname = () =>
  typeof window !== 'undefined' ? asString(window.location?.hostname).toLowerCase() : '';
const isAllowedMixpanelHostname = () => MIXPANEL_ALLOWED_HOSTNAMES.has(getCurrentHostname());
const isMixpanelDebugLoggingEnabled = () =>
  process.env.REACT_APP_ENV === 'development' || process.env.NODE_ENV === 'development';

const logMixpanelEndpointForDebug = enabled => {
  if (!isMixpanelDebugLoggingEnabled() || hasLoggedMixpanelEndpoint) {
    return;
  }

  // Safe for local verification: logs endpoint only, never token or user data.
  console.info('[Mixpanel] api_host', {
    apiHost: MIXPANEL_API_HOST,
    enabled,
  });
  hasLoggedMixpanelEndpoint = true;
};

const getSharetribeUserId = currentUser =>
  asString(currentUser?.id?.uuid || (typeof currentUser?.id === 'string' ? currentUser?.id : ''));

const getUserPublicData = currentUser =>
  currentUser?.attributes?.profile?.publicData || currentUser?.attributes?.publicData || {};

const sanitizeProperties = props =>
  Object.entries(props || {}).reduce((acc, [key, value]) => {
    if (value == null) {
      return acc;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed || trimmed === 'undefined' || trimmed === 'null') {
        return acc;
      }
      acc[key] = trimmed;
      return acc;
    }

    acc[key] = value;
    return acc;
  }, {});

const getMixpanelUserProperties = currentUser => {
  const firstName = asString(currentUser?.attributes?.profile?.firstName);
  const lastName = asString(currentUser?.attributes?.profile?.lastName);
  const publicData = getUserPublicData(currentUser);
  const fullNameFromNames = [firstName, lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  const fullName =
    asString(publicData?.fullName) ||
    asString(currentUser?.attributes?.profile?.displayName) ||
    fullNameFromNames;
  const email = asString(currentUser?.attributes?.email);
  const sharetribeUserId = getSharetribeUserId(currentUser);
  const signupSource = asString(publicData?.signupSource);

  return {
    $email: email,
    $first_name: firstName,
    $last_name: lastName,
    $name: fullName,
    user_id: sharetribeUserId,
    sharetribeUserId,
    is_authenticated: Boolean(sharetribeUserId),
    ...(signupSource ? { signupSource } : {}),
  };
};

export const isMixpanelEnabled = () =>
  process.env.REACT_APP_ENV === 'production' && isAllowedMixpanelHostname();

export const initMixpanel = () => {
  const enabled = isMixpanelEnabled();
  logMixpanelEndpointForDebug(enabled);

  if (!enabled || isInitialized) {
    return;
  }

  mixpanel.init(MIXPANEL_TOKEN, { api_host: MIXPANEL_API_HOST });
  mixpanel.register(getMixpanelPlatformProperties());
  isInitialized = true;
};

export const identifyMixpanelUser = currentUser => {
  if (!isMixpanelEnabled()) {
    return;
  }

  initMixpanel();

  const userProps = getMixpanelUserProperties(currentUser);
  const { sharetribeUserId } = userProps;

  if (!sharetribeUserId) {
    return;
  }

  const nextUserPropsSignature = JSON.stringify(userProps);
  const userChanged = identifiedSharetribeUserId !== sharetribeUserId;
  const userPropertiesChanged = nextUserPropsSignature !== lastRegisteredUserProps;

  if (!userChanged && !userPropertiesChanged) {
    return;
  }

  if (userChanged) {
    mixpanel.identify(sharetribeUserId);
  }

  const profileProps = sanitizeProperties({
    ...userProps,
    ...getMixpanelPlatformProperties(),
  });
  const registeredProps = sanitizeProperties({
    user_id: sharetribeUserId,
    sharetribeUserId,
    is_authenticated: true,
    ...(userProps.signupSource ? { signupSource: userProps.signupSource } : {}),
    ...getMixpanelPlatformProperties(),
  });

  mixpanel.people.set(profileProps);
  mixpanel.register(registeredProps);

  identifiedSharetribeUserId = sharetribeUserId;
  lastRegisteredUserProps = nextUserPropsSignature;
};

export const trackEvent = (eventName, props = {}) => {
  if (!isMixpanelEnabled() || !eventName) {
    return;
  }

  initMixpanel();
  const trackedUserId = asString(props.user_id) || asString(identifiedSharetribeUserId);
  const eventProperties = sanitizeProperties({
    ...props,
    ...(trackedUserId ? { user_id: trackedUserId } : {}),
    is_authenticated: Boolean(trackedUserId),
    ...getMixpanelPlatformProperties(),
  });
  mixpanel.track(eventName, eventProperties);
};

export const resetMixpanelUser = () => {
  if (!isMixpanelEnabled()) {
    return;
  }

  initMixpanel();
  mixpanel.reset();
  mixpanel.register(getMixpanelPlatformProperties());

  identifiedSharetribeUserId = null;
  lastRegisteredUserProps = null;
};
